import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Search,
  Info,
  CheckCircle,
  XCircle,
} from "lucide-react";

const SearchRotatedArrayVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Target in right part",
      array: [4, 5, 6, 7, 0, 1, 2],
      target: 0,
    },
    {
      id: 2,
      name: "Target in left part",
      array: [4, 5, 6, 7, 0, 1, 2],
      target: 5,
    },
    {
      id: 3,
      name: "Target not found",
      array: [4, 5, 6, 7, 0, 1, 2],
      target: 3,
    },
    { id: 4, name: "Single element", array: [1], target: 1 },
    { id: 5, name: "Two elements, target right", array: [3, 1], target: 1 },
    { id: 6, name: "Two elements, target left", array: [3, 1], target: 3 },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [customTarget, setCustomTarget] = useState("");
  const [array, setArray] = useState(testCases[0].array);
  const [target, setTarget] = useState(testCases[0].target);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code lines
  const pseudoCode = [
    { line: "function search(nums, target):", indent: 0 },
    { line: "left = 0, right = nums.length - 1", indent: 1 },
    { line: "while left <= right:", indent: 1 },
    { line: "mid = floor(left + (right - left) / 2)", indent: 2 },
    { line: "if nums[mid] == target: return mid", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Check if left half is sorted", indent: 2 },
    { line: "if nums[left] <= nums[mid]:", indent: 2 },
    { line: "if target > nums[mid] or target < nums[left]:", indent: 3 },
    { line: "left = mid + 1", indent: 4 },
    { line: "else:", indent: 3 },
    { line: "right = mid - 1", indent: 4 },
    { line: "else: // Right half is sorted", indent: 2 },
    { line: "if target < nums[mid] or target > nums[right]:", indent: 3 },
    { line: "right = mid - 1", indent: 4 },
    { line: "else:", indent: 3 },
    { line: "left = mid + 1", indent: 4 },
    { line: "return -1", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  const calculateSteps = (nums, target) => {
    const allSteps = [];
    let stepCount = 0;
    let left = 0;
    let right = nums.length - 1;
    let result = -1;

    const addStep = (desc, hl, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description: desc,
        highlightLine: hl,
        left,
        right,
        result,
        ...details,
      });
    };

    addStep("Start: Initialize left and right pointers.", 1);

    while (left <= right) {
      const mid = Math.floor(left + (right - left) / 2);
      addStep(`Calculate mid index: ${mid}.`, 3, { mid });

      if (nums[mid] === target) {
        result = mid;
        addStep(`Target ${target} found at index ${mid}.`, 4, {
          mid,
          foundIndex: mid,
          isFinal: true,
        });
        return allSteps;
      }
      addStep(`nums[${mid}] (${nums[mid]}) is not the target.`, 4, { mid });

      // Left half sorted
      if (nums[left] <= nums[mid]) {
        addStep(`Left half [${left}...${mid}] is sorted.`, 7, { mid });
        if (target > nums[mid] || target < nums[left]) {
          addStep(`Target is not in the sorted left half. Search right.`, 8, {
            mid,
          });
          left = mid + 1;
          addStep(`Move left pointer to ${left}.`, 9, { mid });
        } else {
          addStep(`Target is in the sorted left half. Search left.`, 10, {
            mid,
          });
          right = mid - 1;
          addStep(`Move right pointer to ${right}.`, 11, { mid });
        }
      }
      // Right half sorted
      else {
        addStep(`Right half [${mid}...${right}] is sorted.`, 12, { mid });
        if (target < nums[mid] || target > nums[right]) {
          addStep(`Target is not in the sorted right half. Search left.`, 13, {
            mid,
          });
          right = mid - 1;
          addStep(`Move right pointer to ${right}.`, 14, { mid });
        } else {
          addStep(`Target is in the sorted right half. Search right.`, 15, {
            mid,
          });
          left = mid + 1;
          addStep(`Move left pointer to ${left}.`, 16, { mid });
        }
      }
    }

    addStep("Left pointer has crossed right. Target not found.", 17, {
      isFinal: true,
      foundIndex: -1,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(array, target);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setHighlightedLine(newSteps.length > 0 ? newSteps[0].highlightLine : -1);
  }, [array, target]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(steps.length > 0 ? steps[0].highlightLine : -1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setArray(testCases[index].array);
    setTarget(testCases[index].target);
  };

  const addCustomTestCase = () => {
    try {
      const nums = JSON.parse(`[${customInput}]`);
      const targetNum = parseInt(customTarget);
      if (nums.some(isNaN) || isNaN(targetNum))
        throw new Error("Invalid number format.");
      const newTestCase = {
        id: Date.now(),
        name: `Custom`,
        array: nums,
        target: targetNum,
      };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
      setCustomTarget("");
    } catch (error) {
      alert("Invalid format. Use comma-separated numbers for array.");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || {};

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Search className="text-blue-500" /> Search in Rotated Sorted Array
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given a sorted integer array `nums` that has been rotated at
                some pivot, and an integer `target`, search for `target` in
                `nums`. If `target` exists, return its index, otherwise return
                -1.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      nums: [{tc.array.join(", ")}], target: {tc.target}
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Array: 4,5,6,7,0,1,2"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="number"
                value={customTarget}
                onChange={(e) => setCustomTarget(e.target.value)}
                placeholder="Target: 0"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4 relative h-20">
              <div className="flex flex-wrap gap-2 items-end">
                {array.map((num, idx) => {
                  const isFound = currentStepData.foundIndex === idx;
                  const isMid = currentStepData.mid === idx;
                  const inRange =
                    idx >= currentStepData.left && idx <= currentStepData.right;

                  return (
                    <div key={idx} className="text-center relative">
                      <div
                        className={`w-12 h-12 flex items-center justify-center border-2 rounded font-mono text-lg font-bold transition-all duration-300
                        ${
                          isFound
                            ? "bg-green-200 border-green-400 scale-110"
                            : isMid
                            ? "bg-purple-200 border-purple-400 scale-110"
                            : inRange
                            ? "bg-blue-100 border-blue-300"
                            : "bg-gray-100 border-gray-300 opacity-50"
                        }`}
                      >
                        {num}
                      </div>
                      <span className="text-xs text-gray-500 mt-1">{idx}</span>
                      <div className="absolute -bottom-5 text-xs font-bold w-full text-center">
                        {currentStepData.left === idx && (
                          <span className="text-blue-600">L</span>
                        )}
                        {currentStepData.right === idx && (
                          <span className="text-blue-600">R</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {currentStepData.isFinal && (
              <div
                className={`mt-4 p-3 rounded-lg text-center ${
                  currentStepData.foundIndex > -1
                    ? "bg-green-100"
                    : "bg-red-100"
                }`}
              >
                <div
                  className={`flex items-center justify-center font-bold ${
                    currentStepData.foundIndex > -1
                      ? "text-green-700"
                      : "text-red-700"
                  }`}
                >
                  {currentStepData.foundIndex > -1 ? (
                    <CheckCircle className="mr-2" />
                  ) : (
                    <XCircle className="mr-2" />
                  )}
                  Result:{" "}
                  {currentStepData.foundIndex > -1
                    ? `Found at index ${currentStepData.foundIndex}`
                    : "Not Found"}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              This is a modified binary search. The key is to realize that in a
              rotated sorted array, at least one half (from `left` to `mid` or
              from `mid` to `right`) is always sorted. We first identify which
              half is sorted. Then, we check if the target lies within the range
              of that sorted half. If it does, we search in that half;
              otherwise, we search in the other half.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(log N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(1)
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The core of binary search (halving the search space) is
                  maintained.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The condition `nums[left] &lt;= nums[mid]` reliably tells you
                  if the left part is the sorted one.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Once the sorted half is identified, a simple range check
                  determines which direction to continue the search.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchRotatedArrayVisualizer;
